import { Timestamp } from '@angular/fire/firestore';

export function convertTimestampToMillis(timestamp: Timestamp | number | string | any | undefined): number | undefined {
  if (!timestamp) {
    return undefined;
  }

  // Already a number (milliseconds)
  if (typeof timestamp === 'number') {
    return timestamp;
  }

  // Firestore Timestamp with toMillis method
  if (typeof timestamp === 'object' && 'toMillis' in timestamp && typeof timestamp.toMillis === 'function') {
    return timestamp.toMillis();
  }

  // Timestamp-like object with seconds property
  if (typeof timestamp === 'object' && timestamp?.seconds && typeof timestamp.seconds === 'number') {
    return timestamp.seconds * 1000;
  }

  // String date (try to parse)
  if (typeof timestamp === 'string') {
    const parsed = Date.parse(timestamp);
    return isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
}

export function convertTimestampToDateString(dateValue: string | Date | Timestamp | null | undefined): string | null {
  if (!dateValue) {
    return null;
  }

  // Already a string
  if (typeof dateValue === 'string') {
    return dateValue;
  }

  // Native JavaScript Date object (from Material Datepicker)
  if (dateValue instanceof Date) {
    if (isNaN(dateValue.getTime())) {
      return null;
    }
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Firestore Timestamp with toDate method
  if (typeof dateValue === 'object') {
    if ('toDate' in dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toISOString().split('T')[0];
    }

    // Timestamp-like object with seconds property
    if ((dateValue as any).seconds) {
      return new Date((dateValue as any).seconds * 1000).toISOString().split('T')[0];
    }
  }

  return null;
}

export function convertDateStringToLocalDate(dateValue: string | null | undefined): Date | null {
  if (!dateValue) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
  if (!match) {
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  const [, year, month, day] = match;
  const numericYear = Number(year);
  const numericMonth = Number(month) - 1;
  const numericDay = Number(day);
  const date = new Date(numericYear, numericMonth, numericDay);
  if (date.getFullYear() !== numericYear || date.getMonth() !== numericMonth || date.getDate() !== numericDay) {
    return null;
  }

  return date;
}

export function convertDateToTimestamp(dateValue: string | null): string | Timestamp | null {
  if (!dateValue) {
    return null;
  }

  try {
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return Timestamp.fromDate(date);
    }
  } catch (error) {
    console.warn('Failed to convert date to Timestamp:', error);
  }

  return dateValue;
}
